import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { ArrowLeft, Palette, FileText, Eye, EyeOff, Save, Check, Plus, Trash2, Mail, ChevronDown, ChevronUp, Info, Upload, Copy, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '@/context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const THEMES = [
  { id: 'default',    name: 'Forest Green',    primary: '#1A4D2E', accent: '#D4A373', bg: '#F5F5F0', desc: 'Classic green, warm gold accents' },
  { id: 'ocean',      name: 'Ocean Blue',      primary: '#1B3A5C', accent: '#F0A500', bg: '#F0F4F8', desc: 'Deep navy, golden highlights' },
  { id: 'slate',      name: 'Modern Slate',    primary: '#334155', accent: '#6366F1', bg: '#F8FAFC', desc: 'Clean slate, indigo accents' },
  { id: 'burgundy',   name: 'Burgundy Rose',   primary: '#7A1F3D', accent: '#D4A373', bg: '#FDF8F5', desc: 'Rich burgundy, rose tones' },
  { id: 'charcoal',   name: 'Charcoal & Teal', primary: '#2D3436', accent: '#00B894', bg: '#F7F9FA', desc: 'Dark charcoal, fresh teal' },
  { id: 'terracotta', name: 'Terracotta Sun',  primary: '#A0522D', accent: '#DAA520', bg: '#FFF8F0', desc: 'Warm earth, golden sun' },
];

const PAGE_LABELS = { about: 'About Us', career: 'Careers', faq: 'FAQ', contact: 'Contact Us' };

const SiteSettings = () => {
  const navigate = useNavigate();
  const { theme: activeTheme, setTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('pages');
  const [pages, setPages] = useState({});
  const [pagesEnabled, setPagesEnabled] = useState({});
  const [contactInfo, setContactInfo] = useState({ contact_email: '', contact_phone: '', contact_address: '' });
  const [editingPage, setEditingPage] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingContact, setIsSavingContact] = useState(false);

  // Custom pages state
  const [customPages, setCustomPages] = useState([]);
  const [showAddPage, setShowAddPage] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageContent, setNewPageContent] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingCustomPage, setEditingCustomPage] = useState(null);
  const [editCustomTitle, setEditCustomTitle] = useState('');
  const [editCustomContent, setEditCustomContent] = useState('');
  const [isSavingCustom, setIsSavingCustom] = useState(false);

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({});
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [adminFiles, setAdminFiles] = useState([]);

  const fetchSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      const [settingsRes, customPagesRes, aboutRes, careerRes, faqRes, contactRes, templatesRes, adminFilesRes] = await Promise.all([
        axios.get(`${API}/site-settings`),
        axios.get(`${API}/custom-pages/all`, { headers }),
        axios.get(`${API}/pages/about`).catch(() => ({ data: {} })),
        axios.get(`${API}/pages/career`).catch(() => ({ data: {} })),
        axios.get(`${API}/pages/faq`).catch(() => ({ data: {} })),
        axios.get(`${API}/pages/contact`).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/email-templates`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/files`, { headers }).catch(() => ({ data: [] })),
      ]);

      const data = settingsRes.data;
      setPagesEnabled(data.pages_enabled || {});
      setPages({
        about: aboutRes.data?.title ? { title: aboutRes.data.title, content: aboutRes.data.content } : {},
        career: careerRes.data?.title ? { title: careerRes.data.title, content: careerRes.data.content } : {},
        faq: faqRes.data?.title ? { title: faqRes.data.title, content: faqRes.data.content } : {},
        contact: contactRes.data?.title ? { title: contactRes.data.title, content: contactRes.data.content } : {},
      });
      setContactInfo({
        contact_email: data.contact_email || '',
        contact_phone: data.contact_phone || '',
        contact_address: data.contact_address || '',
      });
      setCustomPages(customPagesRes.data || []);
      setEmailTemplates(templatesRes.data || []);
      setAdminFiles(adminFilesRes.data || []);
    } catch (error) {
      toast.error('Failed to load settings');
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleThemeSelect = async (themeId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/site-settings/theme`, { theme: themeId }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTheme(themeId);
      toast.success('Theme updated!');
    } catch (error) {
      toast.error('Failed to update theme');
    }
  };

  const handleTogglePage = async (key, checked) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/site-settings/pages-enabled`, { [key]: checked }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPagesEnabled((prev) => ({ ...prev, [key]: checked }));
      toast.success(`${PAGE_LABELS[key]} ${checked ? 'shown' : 'hidden'}`);
    } catch (error) {
      toast.error('Failed to toggle page');
    }
  };

  const startEditing = (key) => {
    setEditingPage(key);
    setEditTitle(pages[key]?.title || PAGE_LABELS[key]);
    setEditContent(pages[key]?.content || '');
  };

  const cancelEditing = () => {
    setEditingPage(null);
    setEditTitle('');
    setEditContent('');
  };

  const savePage = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/pages/${editingPage}`, { title: editTitle, content: editContent }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPages((prev) => ({ ...prev, [editingPage]: { title: editTitle, content: editContent } }));
      setEditingPage(null);
      toast.success('Page saved!');
    } catch (error) {
      toast.error('Failed to save page');
    } finally {
      setIsSaving(false);
    }
  };

  const saveContactInfo = async () => {
    setIsSavingContact(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/site-settings/contact-info`, contactInfo, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Contact info saved!');
    } catch (error) {
      toast.error('Failed to save contact info');
    } finally {
      setIsSavingContact(false);
    }
  };

  // ─── Custom Pages handlers ───────────────────────
  const handleCreatePage = async () => {
    if (!newPageTitle.trim()) { toast.error('Title is required'); return; }
    if (!newPageContent.trim()) { toast.error('Content is required'); return; }
    setIsCreating(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(`${API}/admin/custom-pages`, {
        title: newPageTitle, content: newPageContent,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCustomPages((prev) => [...prev, res.data]);
      setShowAddPage(false);
      setNewPageTitle('');
      setNewPageContent('');
      toast.success('Page created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create page');
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleCustomPage = async (page) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(`${API}/admin/custom-pages/${page.id}`, {
        enabled: !page.enabled,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCustomPages((prev) => prev.map((p) => (p.id === page.id ? res.data : p)));
      toast.success(`"${page.title}" ${!page.enabled ? 'shown' : 'hidden'}`);
    } catch (error) {
      toast.error('Failed to toggle page');
    }
  };

  const startEditingCustom = (page) => {
    setEditingCustomPage(page.id);
    setEditCustomTitle(page.title);
    setEditCustomContent(page.content);
  };

  const cancelEditingCustom = () => {
    setEditingCustomPage(null);
    setEditCustomTitle('');
    setEditCustomContent('');
  };

  const saveCustomPage = async () => {
    setIsSavingCustom(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(`${API}/admin/custom-pages/${editingCustomPage}`, {
        title: editCustomTitle, content: editCustomContent,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCustomPages((prev) => prev.map((p) => (p.id === editingCustomPage ? res.data : p)));
      setEditingCustomPage(null);
      toast.success('Page updated!');
    } catch (error) {
      toast.error('Failed to save page');
    } finally {
      setIsSavingCustom(false);
    }
  };

  const handleDeleteCustomPage = async (page) => {
    if (!window.confirm(`Delete "${page.title}"? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/custom-pages/${page.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomPages((prev) => prev.filter((p) => p.id !== page.id));
      toast.success('Page deleted');
    } catch (error) {
      toast.error('Failed to delete page');
    }
  };

  // ─── Email Template handlers ─────────────────────────
  const startEditingTemplate = (tpl) => {
    setEditingTemplate(tpl.key);
    setTemplateForm({
      subject: tpl.subject || '',
      heading: tpl.heading || '',
      greeting: tpl.greeting || '',
      body: tpl.body || '',
      closing: tpl.closing || '',
      sign_off: tpl.sign_off || '',
      footer: tpl.footer || '',
      attachment_urls: tpl.attachment_urls || [],
    });
    setExpandedTemplate(tpl.key);
  };

  const cancelEditingTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({});
  };

  const saveTemplate = async () => {
    setIsSavingTemplate(true);
    try {
      const res = await axios.put(`${API}/admin/email-templates/${editingTemplate}`, templateForm);
      setEmailTemplates((prev) => prev.map((t) => (t.key === editingTemplate ? res.data : t)));
      setEditingTemplate(null);
      toast.success('Email template saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 p-4 md:p-8">
      <div className="max-w-5xl mx-auto" data-testid="site-settings-page">
        <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="mb-4 text-stone-600 hover:text-[#1A4D2E]" data-testid="back-to-admin">
          <ArrowLeft className="mr-2" size={20} /> Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{ fontFamily: '"Playfair Display", serif' }}>
            Site Settings
          </h1>
          <p className="text-stone-600">Manage your app theme, page content, and visibility</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-stone-100 mb-6 w-full sm:w-auto">
            <TabsTrigger value="theme" className="data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white">
              <Palette size={16} className="mr-2" /> Theme
            </TabsTrigger>
            <TabsTrigger value="pages" className="data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white">
              <FileText size={16} className="mr-2" /> Pages
            </TabsTrigger>
            <TabsTrigger value="email" className="data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white" data-testid="email-format-tab">
              <Mail size={16} className="mr-2" /> Email Format
            </TabsTrigger>
            <TabsTrigger value="files" className="data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white" data-testid="upload-files-tab">
              <Upload size={16} className="mr-2" /> Upload Files
            </TabsTrigger>
          </TabsList>

          {/* ── Theme Tab ───────────────────────────── */}
          <TabsContent value="theme">
            <Card className="p-6 bg-white rounded-2xl card-shadow">
              <h2 className="text-xl font-bold text-stone-900 mb-1">App Theme</h2>
              <p className="text-sm text-stone-500 mb-6">Select a color scheme for your application</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {THEMES.map((theme) => {
                  const isSelected = activeTheme === theme.id;
                  return (
                    <button
                      key={theme.id}
                      onClick={() => handleThemeSelect(theme.id)}
                      className={`relative rounded-xl border-2 text-left transition-all hover:shadow-lg overflow-hidden ${
                        isSelected ? 'shadow-lg ring-2' : 'border-stone-200 hover:border-stone-300'
                      }`}
                      style={isSelected ? { borderColor: theme.primary, boxShadow: `0 4px 20px ${theme.primary}20`, '--tw-ring-color': `${theme.primary}30` } : {}}
                      data-testid={`theme-${theme.id}`}
                    >
                      <div className="h-24 relative" style={{ backgroundColor: theme.bg }}>
                        <div className="absolute left-0 top-0 bottom-0 w-12 flex flex-col items-center pt-3 gap-2" style={{ backgroundColor: 'white', borderRight: '1px solid #e5e5e5' }}>
                          <div className="w-5 h-5 rounded" style={{ backgroundColor: theme.primary }} />
                          <div className="w-6 h-1.5 rounded-full" style={{ backgroundColor: theme.primary, opacity: 0.7 }} />
                          <div className="w-6 h-1.5 rounded-full bg-stone-200" />
                          <div className="w-6 h-1.5 rounded-full bg-stone-200" />
                        </div>
                        <div className="ml-14 pt-3 pr-3 space-y-2">
                          <div className="h-2.5 w-20 rounded" style={{ backgroundColor: theme.primary }} />
                          <div className="flex gap-2">
                            <div className="h-10 flex-1 rounded-md bg-white border border-stone-200 shadow-sm" />
                            <div className="h-10 flex-1 rounded-md bg-white border border-stone-200 shadow-sm" />
                          </div>
                        </div>
                        <div className="absolute bottom-2 right-3 w-4 h-4 rounded-full shadow-sm" style={{ backgroundColor: theme.accent }} />
                      </div>
                      <div className="p-4 bg-white border-t border-stone-100">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-semibold text-stone-900 text-sm">{theme.name}</p>
                          {isSelected && (
                            <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: theme.primary }}>
                              <Check size={12} className="text-white" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-stone-500">{theme.desc}</p>
                        <div className="flex gap-1.5 mt-3">
                          <div className="w-5 h-5 rounded-full shadow-inner border border-stone-200" style={{ backgroundColor: theme.primary }} title="Primary" />
                          <div className="w-5 h-5 rounded-full shadow-inner border border-stone-200" style={{ backgroundColor: theme.accent }} title="Accent" />
                          <div className="w-5 h-5 rounded-full shadow-inner border border-stone-300" style={{ backgroundColor: theme.bg }} title="Background" />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>
          </TabsContent>

          {/* ── Pages Tab ───────────────────────────── */}
          <TabsContent value="pages">
            <div className="space-y-6">
              {/* Page Visibility Toggles */}
              <Card className="p-6 bg-white rounded-2xl card-shadow">
                <h2 className="text-xl font-bold text-stone-900 mb-1">Page Visibility</h2>
                <p className="text-sm text-stone-500 mb-5">Toggle to show or hide pages under the About menu</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.entries(PAGE_LABELS).map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl border border-stone-200">
                      <div className="flex items-center gap-3">
                        {pagesEnabled[key] ? <Eye size={18} className="text-[#1A4D2E]" /> : <EyeOff size={18} className="text-stone-400" />}
                        <span className="font-medium text-stone-800">{label}</span>
                      </div>
                      <Switch
                        checked={pagesEnabled[key]}
                        onCheckedChange={(checked) => handleTogglePage(key, checked)}
                        data-testid={`toggle-${key}`}
                      />
                    </div>
                  ))}
                </div>
              </Card>

              {/* Contact Info */}
              <Card className="p-6 bg-white rounded-2xl card-shadow">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-xl font-bold text-stone-900">Contact Information</h2>
                    <p className="text-sm text-stone-500">Displayed on the Contact Us page</p>
                  </div>
                  <Button
                    size="sm"
                    className="bg-[#1A4D2E] hover:bg-[#143D24]"
                    onClick={saveContactInfo}
                    disabled={isSavingContact}
                    data-testid="save-contact-info"
                  >
                    <Save size={14} className="mr-1.5" /> {isSavingContact ? 'Saving...' : 'Save'}
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-1.5 block text-sm font-semibold">Email</Label>
                    <Input
                      value={contactInfo.contact_email}
                      onChange={(e) => setContactInfo({ ...contactInfo, contact_email: e.target.value })}
                      placeholder="support@yourcompany.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-sm font-semibold">Phone</Label>
                    <Input
                      value={contactInfo.contact_phone}
                      onChange={(e) => setContactInfo({ ...contactInfo, contact_phone: e.target.value })}
                      placeholder="+1 (555) 123-4567"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="mb-1.5 block text-sm font-semibold">Address</Label>
                    <textarea
                      value={contactInfo.contact_address}
                      onChange={(e) => setContactInfo({ ...contactInfo, contact_address: e.target.value })}
                      className="w-full min-h-[80px] p-3 border border-stone-300 rounded-lg text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30 focus:border-[#1A4D2E]"
                      placeholder="123 Main St&#10;Suite 100&#10;City, State 12345"
                      data-testid="contact-address-input"
                    />
                  </div>
                </div>
              </Card>

              {/* Built-in Page Content Editors */}
              {Object.entries(PAGE_LABELS).map(([key, label]) => (
                <Card key={key} className="p-6 bg-white rounded-2xl card-shadow" data-testid={`page-editor-${key}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-stone-900">{label}</h3>
                      <p className="text-xs text-stone-500">
                        {pagesEnabled[key] ? 'Visible to visitors' : 'Hidden from visitors'}
                      </p>
                    </div>
                    {editingPage === key ? (
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={cancelEditing}>Cancel</Button>
                        <Button size="sm" className="bg-[#1A4D2E] hover:bg-[#143D24]" onClick={savePage} disabled={isSaving} data-testid={`save-${key}`}>
                          <Save size={14} className="mr-1.5" /> {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                      </div>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => startEditing(key)} data-testid={`edit-${key}`}>
                        Edit
                      </Button>
                    )}
                  </div>

                  {editingPage === key ? (
                    <div className="space-y-4">
                      <div>
                        <Label className="mb-1.5 block text-sm font-semibold">Page Title</Label>
                        <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} data-testid={`title-${key}`} />
                      </div>
                      <div>
                        <Label className="mb-1.5 block text-sm font-semibold">Page Content</Label>
                        <textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="w-full min-h-[300px] p-3 border border-stone-300 rounded-lg text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30 focus:border-[#1A4D2E]"
                          data-testid={`content-${key}`}
                        />
                        <p className="text-xs text-stone-400 mt-1">Use blank lines between paragraphs.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-stone-50 rounded-lg p-4 border border-stone-200">
                      <p className="text-sm font-semibold text-stone-700 mb-2">{pages[key]?.title || label}</p>
                      <div className="text-sm text-stone-600 whitespace-pre-line max-h-48 overflow-y-auto leading-relaxed">
                        {pages[key]?.content || 'No content yet. Click Edit to add content.'}
                      </div>
                    </div>
                  )}
                </Card>
              ))}

              {/* ── Custom Pages Section ───────────────────────────── */}
              <Card className="p-6 bg-white rounded-2xl card-shadow" data-testid="custom-pages-section">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-xl font-bold text-stone-900">Custom Pages</h2>
                    <p className="text-sm text-stone-500">Create additional pages shown under Company on the home page</p>
                  </div>
                  <Button
                    className="bg-[#1A4D2E] hover:bg-[#143D24]"
                    onClick={() => setShowAddPage(true)}
                    data-testid="add-page-btn"
                  >
                    <Plus size={16} className="mr-1.5" /> Add Page
                  </Button>
                </div>

                {customPages.length === 0 ? (
                  <div className="text-center py-10 border-2 border-dashed border-stone-200 rounded-xl">
                    <FileText size={36} className="mx-auto mb-3 text-stone-300" />
                    <p className="text-stone-500 text-sm">No custom pages yet.</p>
                    <p className="text-stone-400 text-xs mt-1">Click "Add Page" to create your first one.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {customPages.map((page) => (
                      <div key={page.id} className="border border-stone-200 rounded-xl overflow-hidden" data-testid={`custom-page-${page.slug}`}>
                        <div className="flex items-center justify-between p-4 bg-stone-50">
                          <div className="flex items-center gap-3">
                            {page.enabled ? <Eye size={16} className="text-[#1A4D2E]" /> : <EyeOff size={16} className="text-stone-400" />}
                            <div>
                              <h4 className="font-semibold text-stone-900 text-sm">{page.title}</h4>
                              <p className="text-xs text-stone-400">/page/{page.slug}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={page.enabled}
                              onCheckedChange={() => handleToggleCustomPage(page)}
                              data-testid={`toggle-custom-${page.slug}`}
                            />
                            {editingCustomPage === page.id ? (
                              <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={cancelEditingCustom}>Cancel</Button>
                                <Button size="sm" className="bg-[#1A4D2E] hover:bg-[#143D24]" onClick={saveCustomPage} disabled={isSavingCustom}>
                                  <Save size={14} className="mr-1.5" /> {isSavingCustom ? 'Saving...' : 'Save'}
                                </Button>
                              </div>
                            ) : (
                              <>
                                <Button variant="outline" size="sm" onClick={() => startEditingCustom(page)}>Edit</Button>
                                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteCustomPage(page)} data-testid={`delete-custom-${page.slug}`}>
                                  <Trash2 size={14} />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="p-4">
                          {editingCustomPage === page.id ? (
                            <div className="space-y-4">
                              <div>
                                <Label className="mb-1.5 block text-sm font-semibold">Page Title</Label>
                                <Input value={editCustomTitle} onChange={(e) => setEditCustomTitle(e.target.value)} />
                              </div>
                              <div>
                                <Label className="mb-1.5 block text-sm font-semibold">Page Content</Label>
                                <textarea
                                  value={editCustomContent}
                                  onChange={(e) => setEditCustomContent(e.target.value)}
                                  className="w-full min-h-[200px] p-3 border border-stone-300 rounded-lg text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30 focus:border-[#1A4D2E]"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-stone-600 whitespace-pre-line max-h-48 overflow-y-auto leading-relaxed">
                              {page.content || 'No content.'}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* ── Email Format Tab ────────────────────── */}
          <TabsContent value="email">
            <div className="space-y-4" data-testid="email-templates-section">
              <Card className="p-6 bg-white rounded-2xl card-shadow">
                <div className="flex items-center gap-3 mb-1">
                  <Mail size={22} className="text-[#1A4D2E]" />
                  <h2 className="text-xl font-bold text-stone-900">Email Templates</h2>
                </div>
                <p className="text-sm text-stone-500 mb-6">Customize the system emails sent from the platform. Use <code className="bg-stone-100 px-1.5 py-0.5 rounded text-xs font-mono">{'{variable_name}'}</code> for dynamic values.</p>

                {emailTemplates.length === 0 ? (
                  <div className="text-center py-10 border-2 border-dashed border-stone-200 rounded-xl">
                    <Mail size={36} className="mx-auto mb-3 text-stone-300" />
                    <p className="text-stone-500 text-sm">No email templates found.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {emailTemplates.map((tpl) => {
                      const isExpanded = expandedTemplate === tpl.key;
                      const isEditing = editingTemplate === tpl.key;
                      return (
                        <div key={tpl.key} className="border border-stone-200 rounded-xl overflow-hidden" data-testid={`email-template-${tpl.key}`}>
                          {/* Header */}
                          <button
                            className="w-full flex items-center justify-between p-4 bg-stone-50 hover:bg-stone-100 transition-colors text-left"
                            onClick={() => setExpandedTemplate(isExpanded ? null : tpl.key)}
                            data-testid={`toggle-template-${tpl.key}`}
                          >
                            <div>
                              <h3 className="font-semibold text-stone-900 text-sm">{tpl.name}</h3>
                              <p className="text-xs text-stone-500 mt-0.5">{tpl.description}</p>
                            </div>
                            {isExpanded ? <ChevronUp size={18} className="text-stone-400" /> : <ChevronDown size={18} className="text-stone-400" />}
                          </button>

                          {/* Expanded Content */}
                          {isExpanded && (
                            <div className="p-4 border-t border-stone-200">
                              {/* Variables hint */}
                              {tpl.variables && (
                                <div className="flex items-start gap-2 mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                                  <Info size={14} className="text-blue-500 mt-0.5 shrink-0" />
                                  <div>
                                    <p className="text-xs font-semibold text-blue-700 mb-1">Available Variables</p>
                                    <p className="text-xs text-blue-600 font-mono leading-relaxed">
                                      {tpl.variables.split(',').map((v) => v.trim()).map((v, i) => (
                                        <span key={i} className="inline-block bg-blue-100 px-1.5 py-0.5 rounded mr-1 mb-1">{`{${v}}`}</span>
                                      ))}
                                    </p>
                                  </div>
                                </div>
                              )}

                              {isEditing ? (
                                <div className="space-y-4">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <Label className="mb-1.5 block text-sm font-semibold">Subject Line</Label>
                                      <Input
                                        value={templateForm.subject}
                                        onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                                        data-testid={`template-subject-${tpl.key}`}
                                      />
                                    </div>
                                    <div>
                                      <Label className="mb-1.5 block text-sm font-semibold">Heading</Label>
                                      <Input
                                        value={templateForm.heading}
                                        onChange={(e) => setTemplateForm({ ...templateForm, heading: e.target.value })}
                                        data-testid={`template-heading-${tpl.key}`}
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <Label className="mb-1.5 block text-sm font-semibold">Greeting</Label>
                                    <Input
                                      value={templateForm.greeting}
                                      onChange={(e) => setTemplateForm({ ...templateForm, greeting: e.target.value })}
                                      placeholder="e.g. Dear {lead_name},"
                                      data-testid={`template-greeting-${tpl.key}`}
                                    />
                                  </div>
                                  <div>
                                    <Label className="mb-1.5 block text-sm font-semibold">Body</Label>
                                    <textarea
                                      value={templateForm.body}
                                      onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                                      className="w-full min-h-[100px] p-3 border border-stone-300 rounded-lg text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30 focus:border-[#1A4D2E]"
                                      data-testid={`template-body-${tpl.key}`}
                                    />
                                    <p className="text-xs text-stone-400 mt-1">HTML tags like &lt;strong&gt; are supported.</p>
                                  </div>
                                  <div>
                                    <Label className="mb-1.5 block text-sm font-semibold">Closing</Label>
                                    <textarea
                                      value={templateForm.closing}
                                      onChange={(e) => setTemplateForm({ ...templateForm, closing: e.target.value })}
                                      className="w-full min-h-[60px] p-3 border border-stone-300 rounded-lg text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30 focus:border-[#1A4D2E]"
                                      data-testid={`template-closing-${tpl.key}`}
                                    />
                                  </div>
                                  <div>
                                    <Label className="mb-1.5 block text-sm font-semibold">Sign Off</Label>
                                    <textarea
                                      value={templateForm.sign_off}
                                      onChange={(e) => setTemplateForm({ ...templateForm, sign_off: e.target.value })}
                                      placeholder="e.g. Best regards,"
                                      className="w-full min-h-[80px] p-3 border border-stone-300 rounded-lg text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30 focus:border-[#1A4D2E]"
                                      data-testid={`template-signoff-${tpl.key}`}
                                    />
                                  </div>
                                  <div>
                                    <Label className="mb-1.5 block text-sm font-semibold">Footer</Label>
                                    <Input
                                      value={templateForm.footer}
                                      onChange={(e) => setTemplateForm({ ...templateForm, footer: e.target.value })}
                                      data-testid={`template-footer-${tpl.key}`}
                                    />
                                  </div>

                                  {/* Attachments */}
                                  <div>
                                    <Label className="mb-1.5 block text-sm font-semibold">File Attachments</Label>
                                    <p className="text-xs text-stone-400 mb-2">Attach files from your uploaded files. Recipients will receive download links.</p>
                                    {(templateForm.attachment_urls || []).length > 0 && (
                                      <div className="space-y-1.5 mb-3">
                                        {templateForm.attachment_urls.map((att, idx) => (
                                          <div key={idx} className="flex items-center gap-2 bg-stone-50 rounded-lg px-3 py-2 border border-stone-200">
                                            <FileText size={14} className="text-stone-500" />
                                            <span className="text-sm text-stone-700 flex-1 truncate">{att.filename}</span>
                                            <button
                                              onClick={() => setTemplateForm(f => ({
                                                ...f,
                                                attachment_urls: f.attachment_urls.filter((_, i) => i !== idx)
                                              }))}
                                              className="text-red-400 hover:text-red-600"
                                            ><Trash2 size={14} /></button>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {adminFiles.length > 0 ? (
                                      <Select
                                        onValueChange={(fileId) => {
                                          const file = adminFiles.find(f => f.id === fileId);
                                          if (file && !(templateForm.attachment_urls || []).some(a => a.url === file.file_url)) {
                                            setTemplateForm(f => ({
                                              ...f,
                                              attachment_urls: [...(f.attachment_urls || []), { filename: file.original_filename, url: file.file_url }]
                                            }));
                                          }
                                        }}
                                      >
                                        <SelectTrigger className="w-full" data-testid={`attach-file-${tpl.key}`}>
                                          <SelectValue placeholder="Select a file to attach..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {adminFiles.map(f => (
                                            <SelectItem key={f.id} value={f.id}>{f.original_filename}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    ) : (
                                      <p className="text-xs text-stone-400">No files uploaded yet. Go to the Upload Files tab to upload files first.</p>
                                    )}
                                  </div>

                                  <div className="flex gap-2 pt-2">
                                    <Button variant="outline" size="sm" onClick={cancelEditingTemplate}>Cancel</Button>
                                    <Button size="sm" className="bg-[#1A4D2E] hover:bg-[#143D24]" onClick={saveTemplate} disabled={isSavingTemplate} data-testid={`save-template-${tpl.key}`}>
                                      <Save size={14} className="mr-1.5" /> {isSavingTemplate ? 'Saving...' : 'Save Template'}
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div className="bg-stone-50 rounded-lg p-3 border border-stone-200">
                                      <p className="text-xs font-semibold text-stone-500 mb-1">Subject Line</p>
                                      <p className="text-sm text-stone-800 font-mono">{tpl.subject}</p>
                                    </div>
                                    <div className="bg-stone-50 rounded-lg p-3 border border-stone-200">
                                      <p className="text-xs font-semibold text-stone-500 mb-1">Heading</p>
                                      <p className="text-sm text-stone-800">{tpl.heading}</p>
                                    </div>
                                  </div>
                                  <div className="bg-stone-50 rounded-lg p-3 border border-stone-200">
                                    <p className="text-xs font-semibold text-stone-500 mb-1">Greeting</p>
                                    <p className="text-sm text-stone-800 font-mono">{tpl.greeting}</p>
                                  </div>
                                  <div className="bg-stone-50 rounded-lg p-3 border border-stone-200">
                                    <p className="text-xs font-semibold text-stone-500 mb-1">Body</p>
                                    <p className="text-sm text-stone-800" dangerouslySetInnerHTML={{ __html: tpl.body }} />
                                  </div>
                                  <div className="bg-stone-50 rounded-lg p-3 border border-stone-200">
                                    <p className="text-xs font-semibold text-stone-500 mb-1">Closing</p>
                                    <p className="text-sm text-stone-800">{tpl.closing}</p>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div className="bg-stone-50 rounded-lg p-3 border border-stone-200">
                                      <p className="text-xs font-semibold text-stone-500 mb-1">Sign Off</p>
                                      <p className="text-sm text-stone-800 whitespace-pre-line">{tpl.sign_off || '(empty)'}</p>
                                    </div>
                                    <div className="bg-stone-50 rounded-lg p-3 border border-stone-200">
                                      <p className="text-xs font-semibold text-stone-500 mb-1">Footer</p>
                                      <p className="text-sm text-stone-800">{tpl.footer}</p>
                                    </div>
                                  </div>
                                  {(tpl.attachment_urls || []).length > 0 && (
                                    <div className="bg-stone-50 rounded-lg p-3 border border-stone-200">
                                      <p className="text-xs font-semibold text-stone-500 mb-1">Attachments</p>
                                      <div className="space-y-1">
                                        {tpl.attachment_urls.map((att, idx) => (
                                          <a key={idx} href={att.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#1A4D2E] hover:underline">
                                            <FileText size={14} />{att.filename}
                                          </a>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  <Button variant="outline" size="sm" onClick={() => startEditingTemplate(tpl)} data-testid={`edit-template-${tpl.key}`}>
                                    Edit Template
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* ── Upload Files Tab ─────────────────────── */}
          <TabsContent value="files">
            <AdminFileUpload />
          </TabsContent>
        </Tabs>

        {/* Add Page Dialog */}
        <Dialog open={showAddPage} onOpenChange={setShowAddPage}>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#1A4D2E]">Create New Page</DialogTitle>
              <DialogDescription>
                Add a custom page that will appear under "Company" in the home page footer.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label className="mb-1.5 block text-sm font-semibold">Page Title *</Label>
                <Input
                  value={newPageTitle}
                  onChange={(e) => setNewPageTitle(e.target.value)}
                  placeholder="e.g., About Us, Privacy Policy, Terms of Service"
                  data-testid="new-page-title"
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-sm font-semibold">Page Content *</Label>
                <textarea
                  value={newPageContent}
                  onChange={(e) => setNewPageContent(e.target.value)}
                  className="w-full min-h-[250px] p-3 border border-stone-300 rounded-lg text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30 focus:border-[#1A4D2E]"
                  placeholder="Write your page content here. Use blank lines for paragraph breaks."
                  data-testid="new-page-content"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => { setShowAddPage(false); setNewPageTitle(''); setNewPageContent(''); }}>
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-[#1A4D2E] hover:bg-[#143D24]"
                  onClick={handleCreatePage}
                  disabled={isCreating}
                  data-testid="create-page-submit"
                >
                  {isCreating ? 'Creating...' : 'Create Page'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

function AdminFileUpload() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = React.useRef(null);
  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchFiles = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/files`, { headers });
      setFiles(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleUpload = async (e) => {
    const uploadFiles = Array.from(e.target.files);
    if (!uploadFiles.length) return;
    setUploading(true);
    let success = 0;
    for (const file of uploadFiles) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        await axios.post(`${BACKEND_URL}/api/admin/files/upload`, fd, { headers });
        success++;
      } catch (err) {
        toast.error(`Failed: ${file.name}`);
      }
    }
    if (success) { toast.success(`${success} file(s) uploaded`); fetchFiles(); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/files/${id}`, { headers });
      toast.success('File deleted');
      fetchFiles();
    } catch { toast.error('Failed to delete'); }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Card className="p-6 bg-white rounded-2xl card-shadow">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-stone-900 mb-1">Upload Files</h2>
          <p className="text-sm text-stone-500">Upload files to cloud storage and copy shareable URLs</p>
        </div>
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="bg-[#1A4D2E] text-white hover:bg-[#143D24] rounded-full"
          data-testid="admin-upload-btn"
        >
          {uploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
          {uploading ? 'Uploading...' : 'Upload Files'}
        </Button>
        <input ref={fileInputRef} type="file" multiple onChange={handleUpload} style={{ display: 'none' }} />
      </div>

      {loading ? (
        <p className="text-stone-500 text-center py-8">Loading files...</p>
      ) : files.length === 0 ? (
        <div className="text-center py-12 text-stone-400">
          <Upload size={40} className="mx-auto mb-3" />
          <p>No files uploaded yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg border border-stone-200 group" data-testid={`admin-file-${f.id}`}>
              <FileText size={18} className="text-stone-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-stone-800 truncate">{f.original_filename}</p>
                <p className="text-xs text-stone-400">{formatSize(f.file_size)} &middot; {new Date(f.uploaded_at).toLocaleDateString()}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyUrl(f.file_url)}
                className="text-[#1A4D2E] border-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white rounded-full flex-shrink-0"
                data-testid={`copy-url-${f.id}`}
              >
                <Copy size={14} className="mr-1" /> Copy URL
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(f.id)}
                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`delete-file-${f.id}`}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default SiteSettings;
